import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Search, X, AlertTriangle, Clock, Box, ChevronDown, ChevronRight, Filter } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

// Revision Action Dialog
function RevisionActionDialog({ isOpen, onClose, nodeData }) {
  if (!isOpen || !nodeData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Revision Actions</h2>
              <p className="text-orange-100 text-sm mt-1">
                {nodeData.workflow_item_name || 'Node'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Node Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Node Information</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">Node ID</p>
                  <p className="text-sm font-mono text-gray-900 break-all">{nodeData.id}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">Workflow ID</p>
                  <p className="text-sm font-mono text-gray-900 break-all">{nodeData.workflow_id}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">Workflow Item</p>
                  <p className="text-sm font-semibold text-gray-900">{nodeData.workflow_item_name || 'Unknown'}</p>
                  {nodeData.workflow_item_identifier && (
                    <p className="text-xs text-gray-500 mt-1 font-mono">{nodeData.workflow_item_identifier}</p>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">Status</p>
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border bg-orange-100 text-orange-800 border-orange-200">
                    <AlertTriangle className="w-4 h-4" />
                    {nodeData.status}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">Version</p>
                  <p className="text-sm font-semibold text-gray-900">v{nodeData.version}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">Updated At</p>
                  <p className="text-sm text-gray-900">
                    {nodeData.updated_at ? new Date(nodeData.updated_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Output Data Preview */}
              {nodeData.output_data && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">Output Data Preview</p>
                  <pre className="text-xs text-gray-700 overflow-x-auto">
                    {JSON.stringify(nodeData.output_data, null, 2).slice(0, 500)}
                    {JSON.stringify(nodeData.output_data).length > 500 && '...'}
                  </pre>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons Section */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Actions</h3>
            <div className="space-y-3">
              <button
                className="w-full px-4 py-3 bg-white hover:bg-gray-50 border-2 border-gray-300 text-gray-800 rounded-lg transition-colors text-left font-medium flex items-center justify-between"
                onClick={() => alert('Action 1: To be implemented')}
              >
                <span>Action 1 - Placeholder</span>
                <ChevronRight className="w-5 h-5" />
              </button>
              
              <button
                className="w-full px-4 py-3 bg-white hover:bg-gray-50 border-2 border-gray-300 text-gray-800 rounded-lg transition-colors text-left font-medium flex items-center justify-between"
                onClick={() => alert('Action 2: To be implemented')}
              >
                <span>Action 2 - Placeholder</span>
                <ChevronRight className="w-5 h-5" />
              </button>
              
              <button
                className="w-full px-4 py-3 bg-white hover:bg-gray-50 border-2 border-gray-300 text-gray-800 rounded-lg transition-colors text-left font-medium flex items-center justify-between"
                onClick={() => alert('Action 3: To be implemented')}
              >
                <span>Action 3 - Placeholder</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RevisionNodesPage() {
  const { authenticatedFetch } = useAuth();
  
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filter states
  const [workflowItemFilter, setWorkflowItemFilter] = useState('all');
  const [availableWorkflowItems, setAvailableWorkflowItems] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog state
  const [selectedNode, setSelectedNode] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const observerTarget = useRef(null);
  const pageSize = 50;

  const fetchNodes = useCallback(async (page) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      if (workflowItemFilter && workflowItemFilter !== 'all') {
        params.append('workflow_item_identifier', workflowItemFilter);
      }

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/revision/list?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Update available workflow items from API response (only on first page to avoid flickering)
      if (page === 1 && data.available_identifiers) {
        console.log('Available identifiers from API:', data.available_identifiers);
        setAvailableWorkflowItems(data.available_identifiers);
      }
      
      if (page === 1) {
        setNodes(data.nodes);
      } else {
        setNodes(prev => [...prev, ...data.nodes]);
      }
      
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
      setHasMore(page < data.total_pages);
      setCurrentPage(page);
      
    } catch (err) {
      console.error('Error fetching nodes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, loading, workflowItemFilter]);

  // Initial load
  useEffect(() => {
    fetchNodes(1);
  }, [workflowItemFilter]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchNodes(currentPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, currentPage, fetchNodes]);

  const handleRefresh = () => {
    setNodes([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchNodes(1);
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setDialogOpen(true);
  };

  const handleFilterChange = (filter) => {
    setWorkflowItemFilter(filter);
    setNodes([]);
    setCurrentPage(1);
    setHasMore(true);
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-orange-600" />
                Nodes Needing Revision
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {totalCount} total nodes requiring attention
                {workflowItemFilter !== 'all' && (
                  <span className="ml-2 text-orange-600">• Filtered by {workflowItemFilter}</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showFilters 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filters</span>
                {workflowItemFilter !== 'all' && (
                  <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">1</span>
                )}
              </button>
              
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Workflow Item:</label>
                <select
                  value={workflowItemFilter}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="all">All Items</option>
                  {availableWorkflowItems.map(item => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
                
                {workflowItemFilter !== 'all' && (
                  <button
                    onClick={() => handleFilterChange('all')}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6">
          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading nodes</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Table */}
          {nodes.length === 0 && !loading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  No nodes need revision
                </h3>
                <p className="text-gray-600">
                  All nodes are in good status!
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Workflow Item
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Node ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Workflow ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Version
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Updated
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {nodes.map((node) => (
                      <tr 
                        key={node.id}
                        onClick={() => handleNodeClick(node)}
                        className="hover:bg-orange-50 transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {node.workflow_item_name || 'Unknown'}
                            </div>
                            {node.workflow_item_identifier && (
                              <div className="text-xs text-gray-500 font-mono mt-1">
                                {node.workflow_item_identifier}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs font-mono text-gray-900">
                            {node.id.slice(0, 8)}...
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-xs font-mono text-gray-900">
                            {node.workflow_id.slice(0, 8)}...
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border bg-orange-100 text-orange-800 border-orange-200">
                            <AlertTriangle className="w-3 h-3" />
                            {node.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-semibold text-gray-900">
                            v{node.version}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Clock className="w-4 h-4" />
                            {node.updated_at ? new Date(node.updated_at).toLocaleString() : 'N/A'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Loading indicator for infinite scroll */}
              <div ref={observerTarget} className="px-6 py-4 text-center">
                {loading && (
                  <div className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                    <span className="text-sm text-gray-600">Loading more...</span>
                  </div>
                )}
                {!hasMore && nodes.length > 0 && (
                  <div className="text-sm text-gray-500">
                    Showing all {totalCount} nodes • Page {currentPage} of {totalPages}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Revision Action Dialog */}
      <RevisionActionDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        nodeData={selectedNode}
      />
    </div>
  );
}

export default RevisionNodesPage;